import React from "react";

import { useTranslation } from "utils";

import { Card, Col, Flex, Icon, Row, TagNormal, Tooltip, Typography } from "components";

import {
  BULK_DATA_DRAWER_MODE,
  BULK_DATA_POINT_UNAVAILABLE_REASON,
  type BulkDataDrawerMode,
  type BulkDataPointState,
  type BulkDataPointType,
  isBulkDataPointSelectable,
} from "pages/app/historical-practice-data-table-view/utils/BulkDataPointUtils";

export type BulkDataPointSelectorProps = {
  mode: BulkDataDrawerMode;
  dataPointStates: BulkDataPointState[];
  selectedPracticeType: BulkDataPointType | undefined;
  // data points holding unsaved changes, which the save writes together
  editedPracticeTypes: BulkDataPointType[];
  labelOverrides?: Partial<Record<BulkDataPointType, string>>;
  onSelect: (practiceType: BulkDataPointType) => void;
};

type BulkDataPointCardProps = {
  mode: BulkDataDrawerMode;
  dataPointState: BulkDataPointState;
  isSelected: boolean;
  isEdited: boolean;
  labelOverride?: string;
  onSelect: (practiceType: BulkDataPointType) => void;
};

export const BulkDataPointSelector = ({
  mode,
  dataPointStates,
  selectedPracticeType,
  editedPracticeTypes,
  labelOverrides = {},
  onSelect,
}: BulkDataPointSelectorProps) => {
  const { t } = useTranslation("historicalPracticeDataTableView");

  return (
    <Flex vertical gap="0.75rem" className="bulk-data-point-selector">
      <Typography variant="h6">{t(mode === BULK_DATA_DRAWER_MODE.add ? "bulkDataDrawer.selectDataPointToAdd" : "bulkDataDrawer.selectDataPoint")}</Typography>
      {/* three per row, as the design lays the grid out — Row/Col rather than a flex row so the cards keep their
          width instead of shrinking to their content. sm is passed explicitly because the shared Col defaults it
          to 24, which would stack the cards one per row */}
      <Row gutter={[8, 8]}>
        {/* prelint-ignore-col */}
        {dataPointStates.map((dataPointState) => (
          <Col span={8} sm={8} key={dataPointState.practiceType}>
            <BulkDataPointCard
              mode={mode}
              dataPointState={dataPointState}
              isSelected={dataPointState.practiceType === selectedPracticeType}
              isEdited={editedPracticeTypes.includes(dataPointState.practiceType)}
              labelOverride={labelOverrides[dataPointState.practiceType]}
              onSelect={onSelect}
            />
          </Col>
        ))}
      </Row>
    </Flex>
  );
};

const BulkDataPointCard = ({ mode, dataPointState, isSelected, isEdited, labelOverride, onSelect }: BulkDataPointCardProps) => {
  const { t } = useTranslation("historicalPracticeDataTableView");
  const { practiceType, iconName, labelKey, writableCount, filledCount, selectedCount, isPartial, unavailableReason } = dataPointState;
  // a verified data point still opens, read-only, so its values can be looked at
  const isDisabled = !isBulkDataPointSelectable(dataPointState);
  const isAddMode = mode === BULK_DATA_DRAWER_MODE.add;
  const label = labelOverride ?? t(`bulkDataDrawer.dataPoints.${labelKey}`);
  const isVerified = unavailableReason === BULK_DATA_POINT_UNAVAILABLE_REASON.verified;
  const isComplete = unavailableReason === BULK_DATA_POINT_UNAVAILABLE_REASON.complete;
  const hasMixedCropNames = unavailableReason === BULK_DATA_POINT_UNAVAILABLE_REASON.mixedCropNames;

  return (
    <Card
      className={`bulk-data-point-card ${isSelected ? "selected" : ""} ${isDisabled ? "disabled" : ""}`}
      dataTestId={`bulk-data-point-card-${practiceType.toLowerCase()}`}
      onClick={isDisabled ? undefined : () => onSelect(practiceType)}
      tabIndex={isDisabled ? -1 : 0}
    >
      {/* the tooltip sits on the card's content rather than the Card itself: Card doesn't forward a ref, so Tooltip
          would wrap it in an inline span and the card would shrink to its text instead of filling the column */}
      <Tooltip
        title={
          hasMixedCropNames
            ? t("bulkDataDrawer.mixedCropNamesTooltip", { dataPoint: label.toLowerCase() })
            : isVerified
            ? t("bulkDataDrawer.verifiedTooltip", { dataPoint: label.toLowerCase() })
            : isComplete
            ? t("bulkDataDrawer.completeTooltip")
            : undefined
        }
      >
        <Flex vertical justify="space-between" gap="0.75rem" className="bulk-data-point-card-content">
          <Flex align="center" justify="space-between" gap="0.5rem">
            <Icon iconName={iconName} className="bulk-data-point-card-icon" />
            {isEdited ? (
              <TagNormal className="bulk-data-point-card-badge" color="green" text={t("bulkDataDrawer.edited")} />
            ) : hasMixedCropNames ? (
              <TagNormal className="bulk-data-point-card-badge" color="grey" text={t("bulkDataDrawer.mixedCropNames")} />
            ) : unavailableReason === BULK_DATA_POINT_UNAVAILABLE_REASON.mixedCropTypes ? (
              <TagNormal className="bulk-data-point-card-badge" color="grey" text={t("bulkDataDrawer.mixedCropTypes")} />
            ) : isAddMode ? (
              // add mode always states how much of the selection already holds a value, green once nothing is left to add
              <TagNormal
                className="bulk-data-point-card-badge"
                color={filledCount === selectedCount ? "green" : "grey"}
                text={t("bulkDataDrawer.filled", { filled: filledCount, total: selectedCount })}
              />
            ) : isVerified ? (
              <TagNormal className="bulk-data-point-card-badge" color="grey" icon="tabler/lock" text={t("bulkDataDrawer.verifiedBadge")} />
            ) : isPartial ? (
              <TagNormal className="bulk-data-point-card-badge" color="grey" text={t("bulkDataDrawer.editableValues", { count: writableCount })} />
            ) : null}
          </Flex>
          <Typography variant="paragraph-m" color={isDisabled ? "grey-600" : undefined} className="bulk-data-point-card-label">
            {label}
          </Typography>
        </Flex>
      </Tooltip>
    </Card>
  );
};
